import { useState } from "react";
import { FileText, Sparkles, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import type { QnaQuestion } from "@/lib/api/fddQa";
import { DEMO_QNA_QUESTIONS } from "./demoQnaQuestions";
import { qnaActionStyle } from "./qnaStyles";
import { demoQnaResponseAnswers } from "./demoQnaResponses";

interface Props {
  kind: "notes" | "responses";
  questions: QnaQuestion[];
  onApply: (answers: { id: string; answer: string }[]) => void;
}

export function DemoQnaUploadDialog({ kind, questions, onApply }: Props) {
  const [open, setOpen] = useState(false);
  const [review, setReview] = useState(false);
  const [text, setText] = useState("");
  const [fileName, setFileName] = useState("");
  const isNotes = kind === "notes";
  const label = isNotes ? "Upload Meeting Notes" : "Upload Q&A Responses";
  const examples = DEMO_QNA_QUESTIONS.filter(
    (example) =>
      example.answer &&
      questions.some((q) => q.id === example.id && q.status !== "Rejected"),
  );
  const previewAnswers = (isNotes ? examples.slice(0, 3) : examples);
  function changeOpen(value: boolean) {
    setOpen(value);
    if (!value) {
      setReview(false);
      setText("");
      setFileName("");
    }
  }
  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogTrigger asChild>
        <button type="button" style={qnaActionStyle}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <rect
              x="1"
              y="1"
              width="14"
              height="14"
              rx="2"
              fill={isNotes ? "#2B579A" : "#217346"}
            />
            <text
              x="8"
              y="11.5"
              textAnchor="middle"
              fontSize="9"
              fontWeight="700"
              fill="#fff"
            >
              {isNotes ? "W" : "X"}
            </text>
          </svg>
          {label}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            {review ? "Review Matched Answers" : label}
          </DialogTitle>
          <DialogDescription>
            Demo preview with example answers. Files stay on your device;
            applying answers updates this demo only.
          </DialogDescription>
        </DialogHeader>
        {review ? (
          <div className="space-y-4 overflow-auto">
            <div className="space-y-3">
              {previewAnswers.map((example) => (
                <div
                  key={example.id}
                  className="p-3 rounded-lg border border-border bg-secondary/30"
                >
                  <p className="text-xs text-muted-foreground mb-1">Question</p>
                  <p className="text-sm font-medium mb-2">
                    {questions.find((q) => q.id === example.id)?.question}
                  </p>
                  <p className="text-xs text-muted-foreground mb-1">
                    Example answer
                  </p>
                  <p className="text-sm bg-success-bg/50 p-2 rounded border border-success/20">
                    {example.answer}
                  </p>
                </div>
              ))}
            </div>
            {!examples.length && (
              <p className="text-sm text-muted-foreground">
                The sample questions have been removed from this demo.
              </p>
            )}
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="ghost" onClick={() => setReview(false)}>
                Back
              </Button>
              <Button
                disabled={!examples.length}
                onClick={() => {
                  onApply(
                    previewAnswers.map((q) => ({ id: q.id, answer: q.answer! })),
                  );
                  changeOpen(false);
                }}
              >
                Apply {previewAnswers.length} Answers
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 overflow-auto">
            <label className="block border-2 border-dashed border-border rounded-lg p-4 text-center cursor-pointer">
              <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <span className="text-sm">{fileName || "Click to upload"}</span>
              <span className="block text-xs text-muted-foreground mt-1">
                {isNotes ? "Word, TXT or Markdown" : "Excel, CSV, Word or TXT"}
              </span>
              <input
                aria-label={
                  isNotes ? "Meeting notes file" : "Q&A responses file"
                }
                type="file"
                className="sr-only"
                accept={
                  isNotes
                    ? ".doc,.docx,.txt,.md"
                    : ".xlsx,.csv,.doc,.docx,.txt,.md"
                }
                onChange={(e) => setFileName(e.target.files?.[0]?.name ?? "")}
              />
            </label>
            <div className="text-center text-xs text-muted-foreground">
              or paste content below
            </div>
            <Textarea
              aria-label={isNotes ? "Meeting notes" : "Q&A responses"}
              value={text}
              maxLength={50000}
              onChange={(e) => setText(e.target.value)}
              placeholder={
                isNotes
                  ? "Paste your meeting notes here..."
                  : "Paste the vendor’s Q&A responses here..."
              }
              className="min-h-[160px] resize-none text-sm"
            />
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="ghost" onClick={() => changeOpen(false)}>
                Cancel
              </Button>
              <Button
                disabled={!fileName && !text.trim()}
                onClick={() => {
                  onApply(
                    isNotes
                      ? previewAnswers.map((q) => ({ id: q.id, answer: q.answer! }))
                      : demoQnaResponseAnswers(questions, text),
                  );
                  changeOpen(false);
                }}
                className="gap-2"
              >
                <Upload className="w-4 h-4" />
                Upload
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
